import { useEffect, useReducer, useRef, useState } from "react";
import AppButton from "../elements/AppButton";
import MoneyInput from "../elements/MoneyInput";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { showToastMessage } from "../../features/generalSlice";
import { toast } from "react-toastify";
import DropFile, { DropFileFunctions } from "../elements/DropFile";
import { selectAccessToken } from "../../features/auth/authSlice";
import useApiRequest from "../../helpers/api_request";
import { BASE_URL } from "../../helpers/constants";
import { AxiosError } from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppSpinner from "../elements/AppSpinner";
import {
  AddtionalInputsModal,
  LogoModal,
  TypeExtraInput,
} from "../payment_links/NewPaymentLink";

import { getFileNameFromUlr, notEmpty } from "../../helpers/commons";
import {
  TypeEventsTicketItem,
  TypeTicketType,
} from "../../features/eventsticketitems/eventsTicketItemsSlice";

/* ------------------------------------------------------------------ */
/* Time options - 30 minute steps, 00:00 through 23:30                 */
/* Stored as "HH:mm", shown as "9:00 AM"                               */
/* ------------------------------------------------------------------ */

const DEFAULT_START_TIME = "09:00";
const DEFAULT_END_TIME = "17:00";

const buildTimeOptions = () => {
  const options: { value: string; label: string }[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 30) {
    const hour24 = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const value =
      String(hour24).padStart(2, "0") + ":" + String(minute).padStart(2, "0");

    const suffix = hour24 < 12 ? "AM" : "PM";
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    const label = hour12 + ":" + String(minute).padStart(2, "0") + " " + suffix;

    options.push({ value, label });
  }
  return options;
};

const TIME_OPTIONS = buildTimeOptions();

/* ------------------------------------------------------------------ */
/* Extra inputs (same reducer as the digital items form)               */
/* ------------------------------------------------------------------ */

type TypeExtraInputState = TypeExtraInput[];
type TypeExtraInputAction = {
  type: string;
  payload: {
    data?: TypeExtraInput;
    index?: number;
  };
};

const extraInputsReducer = (
  state: TypeExtraInputState,
  action: TypeExtraInputAction
): TypeExtraInputState => {
  switch (action.type) {
    case "add":
      return action.payload.data ? [...state, action.payload.data] : state;
    case "remove":
      if (typeof action.payload.index === "number") {
        return state.filter((input, index) => index !== action.payload.index);
      }
      return state;
    case "edit":
      if (typeof action.payload.index === "number" && action.payload.data) {
        return state.map((item, index) => {
          if (index === action.payload.index) {
            return { ...item, ...action.payload.data };
          }
          return item;
        });
      }
      return state;
    default:
      return state;
  }
};

/* ------------------------------------------------------------------ */
/* Ticket types (Standard Entry, Group Entry, ...)                     */
/* TypeTicketType is imported from the slice so the id survives edits  */
/* ------------------------------------------------------------------ */

type TypeTicketTypeState = TypeTicketType[];
type TypeTicketTypeAction = {
  type: string;
  payload: {
    data?: TypeTicketType;
    index?: number;
  };
};

const ticketTypesReducer = (
  state: TypeTicketTypeState,
  action: TypeTicketTypeAction
): TypeTicketTypeState => {
  switch (action.type) {
    case "add":
      return action.payload.data ? [...state, action.payload.data] : state;
    case "remove":
      if (typeof action.payload.index === "number") {
        return state.filter((ticket, index) => index !== action.payload.index);
      }
      return state;
    case "edit":
      if (typeof action.payload.index === "number" && action.payload.data) {
        return state.map((item, index) => {
          if (index === action.payload.index) {
            return { ...item, ...action.payload.data };
          }
          return item;
        });
      }
      return state;
    default:
      return state;
  }
};

/* ------------------------------------------------------------------ */
/* Ticket type modal                                                   */
/* ------------------------------------------------------------------ */

type TicketTypeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  ticketTypeAdded: (ticketType: TypeTicketType, index: number | null) => void;
  initialTicket?: TypeTicketType;
  editingIndex: number | null;
};

export const TicketTypeModal = ({
  isOpen,
  onClose,
  ticketTypeAdded,
  initialTicket,
  editingIndex,
}: TicketTypeModalProps) => {
  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [minQty, setMinQty] = useState(1);
  const [maxQty, setMaxQty] = useState(10);
  const [capacity, setCapacity] = useState(0);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    if (initialTicket) {
      setName(initialTicket.name);
      setPrice(Number(initialTicket.price) || 0);
      setOriginalPrice(Number(initialTicket.original_price) || 0);
      setMinQty(Number(initialTicket.min_qty) || 1);
      setMaxQty(Number(initialTicket.max_qty) || 10);
      setCapacity(Number(initialTicket.capacity) || 0);
      setNote(initialTicket.note || "");
    } else {
      setName("");
      setPrice(0);
      setOriginalPrice(0);
      setMinQty(1);
      setMaxQty(10);
      setCapacity(0);
      setNote("");
    }
  }, [isOpen, initialTicket]);

  const onSaveTicketType = () => {
    if (name.trim() === "") {
      toast.error("Ticket name is missing");
      return;
    }
    if (price === null || price === undefined || isNaN(price) || price < 0) {
      toast.error("Enter a valid ticket price");
      return;
    }
    if (minQty < 1) {
      toast.error("Minimum per order must be at least 1");
      return;
    }
    if (maxQty < minQty) {
      toast.error("Maximum per order cannot be less than the minimum");
      return;
    }

    ticketTypeAdded(
      {
        // spread first so an existing ticket keeps its id and position -
        // the backend upserts on id, and a missing one creates a duplicate
        ...(initialTicket || {}),
        name: name.trim(),
        price,
        original_price: originalPrice,
        min_qty: minQty,
        max_qty: maxQty,
        capacity,
        note: note.trim(),
      },
      editingIndex
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-40"
        onClick={onClose}
      ></div>
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h5 className="card-title">
            {editingIndex !== null ? "Edit ticket" : "Add ticket"}
          </h5>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Ticket name *
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            type="text"
            className="form-input-2"
          />
        </div>

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            Price per person (UGX) *
          </label>
          <MoneyInput
            value={price}
            onChange={(value) => setPrice(value)}
            className="form-input-2"
          />
          <p className="block text-xs font-medium text-gray-700">
            Set price to <span className="text-red-500">0</span> for a free
            ticket.
          </p>
        </div>

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            Original price (UGX)
          </label>
          <MoneyInput
            value={originalPrice}
            onChange={(value) => setOriginalPrice(value)}
            className="form-input-2"
          />
          <p className="block text-xs font-medium text-gray-700">
            Shown struck out. Leave at 0 to hide it.
          </p>
        </div>

        <div className="flex gap-3 mt-3">
          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700">
              Min per order
            </label>
            <input
              value={minQty}
              onChange={(e) => setMinQty(parseInt(e.target.value || "0", 10))}
              type="number"
              min={1}
              className="form-input-2"
            />
          </div>
          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700">
              Max per order
            </label>
            <input
              value={maxQty}
              onChange={(e) => setMaxQty(parseInt(e.target.value || "0", 10))}
              type="number"
              min={1}
              className="form-input-2"
            />
          </div>
        </div>
        <p className="block text-xs font-medium text-gray-700">
          Group tickets: set the min to the group size, e.g.{" "}
          <span className="text-red-500">6</span>.
        </p>

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            Tickets available
          </label>
          <input
            value={capacity}
            onChange={(e) => setCapacity(parseInt(e.target.value || "0", 10))}
            type="number"
            min={0}
            className="form-input-2"
          />
          <p className="block text-xs font-medium text-gray-700">
            Set to <span className="text-red-500">0</span> for unlimited.
          </p>
        </div>

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            Short note
          </label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            type="text"
            className="form-input-2"
          />
          <p className="block text-xs font-medium text-gray-700">
            Shown next to the price, e.g. groups of 6+.
          </p>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onSaveTicketType}
            type="button"
            className="prim-btn-1"
          >
            {editingIndex !== null ? "Save ticket" : "Add ticket"}
          </button>
          <button onClick={onClose} type="button" className="btn-light">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const NewEventsTicketItem = () => {
  const [extraInputs, extraInputDispatch] = useReducer(extraInputsReducer, []);
  const [ticketTypes, ticketTypeDispatch] = useReducer(ticketTypesReducer, []);

  const [showExtraInputsModal, setShowExtraInputsModal] = useState(false);
  const [showTicketTypeModal, setShowTicketTypeModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);

  const [title, setTitle] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [venue, setVenue] = useState("");
  const [eventMode, setEventMode] = useState("physical");
  const [onlineLink, setOnlineLink] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME);
  // the closing day carries its own hours
  const [endDayStartTime, setEndDayStartTime] = useState(DEFAULT_START_TIME);
  const [endDayEndTime, setEndDayEndTime] = useState(DEFAULT_END_TIME);
  const [linkType, setLinkType] = useState("public");
  const [categoryType, setCategoryType] = useState("Festivals & Concerts");

  const [btnLabelText, setBtnLabelText] = useState("Create Event");
  const [eventTitleText, setEventTitleText] = useState("Create");
  // id comes back as a BigInt-serialised string, so allow both
  const [eventId, setEventId] = useState<number | string>(0);

  const [editingInputIndex, setEditingInputIndex] = useState<number | null>(
    null
  );
  const [editingTicketIndex, setEditingTicketIndex] = useState<number | null>(
    null
  );

  const [savingLinkStatus, setSavingLinkStatus] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const apiRequest = useApiRequest();
  const accessToken = useAppSelector(selectAccessToken);
  const { ref } = useParams();
  const [selectedEventTicket, setSelectedEventTicket] =
    useState<TypeEventsTicketItem | null>(null);
  const [isLoadingLink, setIsLoadingLink] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const storageUrl = process.env.REACT_APP_STORAGE_URL;
  const dropFileRef = useRef<DropFileFunctions>(null);

  const extraInputAdded = (extraInput: TypeExtraInput, index: number | null) => {
    if (index !== null) {
      extraInputDispatch({
        type: "edit",
        payload: { data: extraInput, index: index },
      });
    } else {
      extraInputDispatch({
        type: "add",
        payload: { data: extraInput },
      });
    }
    setShowExtraInputsModal(false);
    setEditingInputIndex(null);
  };

  const ticketTypeAdded = (ticketType: TypeTicketType, index: number | null) => {
    if (index !== null) {
      ticketTypeDispatch({
        type: "edit",
        payload: { data: ticketType, index: index },
      });
    } else {
      ticketTypeDispatch({
        type: "add",
        payload: { data: ticketType },
      });
    }
    setShowTicketTypeModal(false);
    setEditingTicketIndex(null);
  };

  useEffect(() => {
    if (ref != undefined && selectedEventTicket === null) {
      getEventTicketDetails();
    }
  }, []);

  const onSaveEventTicket = async () => {
    if (title == null || title === "") {
      toast.error("Event name is missing");
      return;
    }

    if (description == null || description === "") {
      toast.error("Event description is missing");
      return;
    }

    if (eventMode === "physical" && (venue == null || venue === "")) {
      toast.error("Event venue is missing");
      return;
    }

    if (eventMode === "online" && (onlineLink == null || onlineLink === "")) {
      toast.error("Meeting link is missing");
      return;
    }

    if (eventDate == null || eventDate === "") {
      toast.error("Event date is missing");
      return;
    }

    if (endDate !== "" && endDate < eventDate) {
      toast.error("End date cannot be before the start date");
      return;
    }

    if (startTime == null || startTime === "") {
      toast.error("Event start time is missing");
      return;
    }

    if (ticketTypes.length === 0) {
      toast.error("Add at least one ticket");
      return;
    }

    if (imageUrl == null || imageUrl === "") {
      toast.error("Event banner is missing");
      return;
    }

    let message = "Event Added";
    let saveUrl = BASE_URL + "/api/donation-link/create_event_ticket";
    if (selectedEventTicket !== null) {
      saveUrl =
        BASE_URL +
        "/api/donation-link/update_event_ticket/" +
        selectedEventTicket.id;
      message = "Event Updated";
    }

    setSavingLinkStatus(true);

    apiRequest({
      method: "post",
      url: saveUrl,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
      data: {
        title, // required
        tagline,
        description, // required
        image: imageUrl || null,
        venue,
        event_mode: eventMode,
        online_link: eventMode === "online" ? onlineLink : null,
        event_date: eventDate,
        // a single day event stores the same date in both, so downstream
        // code never has to special case a null end
        end_date: endDate || eventDate,
        start_time: startTime,
        end_time: endTime || null,
        end_day_start_time: endDayStartTime,
        end_day_end_time: endDayEndTime || null,
        category: categoryType,
        type: linkType,
        ticket_types: ticketTypes,
        extra_fields: extraInputs || [],
        seller_note: null,
        buyer_note: null,
      },
    })
      .then((response) => {
        console.log("EVENT-TICKET-CREATE" + JSON.stringify(response));
        dispatch(
          showToastMessage({
            message: message,
            type: "success",
            position: toast.POSITION.TOP_RIGHT,
          })
        );
        setSavingLinkStatus(false);
        navigate("/events-tickets"); // redirect to list
      })
      .catch((error) => {
        console.log("EVENT-TICKETS" + JSON.stringify(error));
        const errorResponse = error as AxiosError;
        if (errorResponse.response) {
          const data = errorResponse.response.data as {
            message: string;
            status: string;
          };
          toast.error(data.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.error("Something went wrong", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        setSavingLinkStatus(false);
      });
  };

  const getEventTicketDetails = async () => {
    setIsLoadingLink(true);
    await apiRequest({
      method: "get",
      url: BASE_URL + "/api/donation-link/details_event_ticket/" + ref,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    })
      .then((response) => {
        console.log("DATAS-EVENT-TICKETS" + JSON.stringify(response));

        setSelectedEventTicket(response.data.details);

        const eventTicket: TypeEventsTicketItem = response.data.details;
        if (eventTicket != null) {
          setBtnLabelText("Update Event");
          setEventTitleText("Update");
          setEventId(eventTicket.id);
          setTitle(eventTicket.title);
          setTagline(eventTicket.tagline ? eventTicket.tagline : "");
          setDescription(
            eventTicket.description ? eventTicket.description : ""
          );
          setVenue(eventTicket.venue ? eventTicket.venue : "");
          setEventMode(eventTicket.event_mode || "physical");
          setOnlineLink(eventTicket.online_link || "");

          // API returns an ISO timestamp; <input type="date"> only accepts
          // YYYY-MM-DD and silently renders blank otherwise
          setEventDate(
            eventTicket.event_date
              ? String(eventTicket.event_date).split("T")[0]
              : ""
          );
          setEndDate(
            eventTicket.end_date
              ? String(eventTicket.end_date).split("T")[0]
              : ""
          );

          // times are stored as HH:mm - trim any seconds so the value
          // matches one of the dropdown options
          setStartTime(
            eventTicket.start_time
              ? String(eventTicket.start_time).slice(0, 5)
              : DEFAULT_START_TIME
          );
          setEndTime(
            eventTicket.end_time ? String(eventTicket.end_time).slice(0, 5) : ""
          );

          setEndDayStartTime(
            eventTicket.end_day_start_time
              ? String(eventTicket.end_day_start_time).slice(0, 5)
              : DEFAULT_START_TIME
          );
          setEndDayEndTime(
            eventTicket.end_day_end_time
              ? String(eventTicket.end_day_end_time).slice(0, 5)
              : ""
          );

          if (eventTicket?.type) {
            const matchingOption = [
              "Public",
              "Private",
              "public",
              "private",
            ].find((opt) => opt === eventTicket.type);

            setLinkType(matchingOption || "");
          }

          if (eventTicket?.category) {
            const matchingOption = [
              "Festivals & Concerts",
              "Parties & Nightlife",
              "Food & Drink",
              "Conferences & Summits",
              "Workshops & Trainings",
              "Sports & Fitness",
              "Church & Fellowship",
              "Weddings & Introductions",
              "Comedy & Theatre",
              "Community & Fundraisers",
            ].find((opt) => opt === eventTicket.category);

            setCategoryType(matchingOption || "");
          }

          setImageUrl(eventTicket.image ? eventTicket.image : "");

          if (eventTicket.ticket_types) {
            for (const ticket_type of eventTicket.ticket_types) {
              ticketTypeDispatch({
                type: "add",
                payload: {
                  data: ticket_type,
                },
              });
            }
          }

          if (eventTicket.extra_fields) {
            for (const extra_field of eventTicket.extra_fields) {
              extraInputDispatch({
                type: "add",
                payload: {
                  data: extra_field,
                },
              });
            }
          }
        }
        setIsLoadingLink(false);
      })
      .catch((error) => {
        const errorResponse = error as AxiosError;
        if (errorResponse.response) {
          const data = errorResponse.response.data as {
            message: string;
            status: string;
          };
          toast.error(data.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.error("Something went wrong", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        setIsLoadingLink(false);
      });
  };

  return (
    <div>
      <div className="card mb-5">
        <div className="card-body flex flex-col px-4 py-4 min-h-half-screen">
          <div className="flex">
            <nav className="flex justify-between " aria-label="Breadcrumb">
              <ol className="inline-flex items-center mb-3 sm:mb-0">
                <li>
                  <div className="flex items-center">
                    <Link to="/events-tickets" className=" card-title ">
                      Events Tickets
                    </Link>
                  </div>
                </li>
                <span className="mx-2 text-gray-400">/</span>
                <li aria-current="page">
                  <div className="flex items-center">
                    <h5 className=" card-title ">{eventTitleText}</h5>
                  </div>
                </li>
              </ol>
            </nav>
          </div>
          {isLoadingLink ? (
            <div>
              <AppSpinner />
            </div>
          ) : (
            <div className=" h-fit flex-grow  ">
              <div className=" w-2/3 mt-3">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 "
                >
                  Event name *
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  type="text"
                  id="title"
                  className="form-input-2"
                />
              </div>

              <div className=" w-2/3 mt-4">
                <label
                  htmlFor="tagline"
                  className="block text-sm font-medium text-gray-700 "
                >
                  Tagline
                </label>
                <input
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  type="text"
                  id="tagline"
                  className="form-input-2"
                />
              </div>

              <div className=" w-2/3 mt-4">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 "
                >
                  Description *
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-input-2"
                  id="description"
                  cols={30}
                  rows={7}
                ></textarea>
              </div>

              <div className=" w-2/3 mt-4">
                <label
                  htmlFor="event_mode"
                  className="block text-sm font-medium text-gray-700 "
                >
                  Event Mode *
                </label>
                <select
                  value={eventMode}
                  onChange={(e) => setEventMode(e.target.value)}
                  className="form-input-2"
                  id="event_mode"
                >
                  <option value="physical">Physical — at a venue</option>
                  <option value="online">Online — joined by link</option>
                </select>
              </div>

              {eventMode === "physical" ? (
                <div className=" w-2/3 mt-4">
                  <label
                    htmlFor="venue"
                    className="block text-sm font-medium text-gray-700 "
                  >
                    Venue *
                  </label>
                  <input
                    value={venue}
                    onChange={(e) => setVenue(e.target.value)}
                    type="text"
                    id="venue"
                    className="form-input-2"
                  />
                </div>
              ) : (
                <>
                  <div className=" w-2/3 mt-4">
                    <label
                      htmlFor="venue"
                      className="block text-sm font-medium text-gray-700 "
                    >
                      Platform
                    </label>
                    <input
                      value={venue}
                      onChange={(e) => setVenue(e.target.value)}
                      type="text"
                      id="venue"
                      className="form-input-2"
                    />
                    <p className="block text-xs font-medium text-gray-700">
                      Shown publicly, e.g. Zoom or Google Meet.
                    </p>
                  </div>

                  <div className=" w-2/3 mt-4">
                    <label
                      htmlFor="online_link"
                      className="block text-sm font-medium text-gray-700 "
                    >
                      Meeting link *
                    </label>
                    <input
                      value={onlineLink}
                      onChange={(e) => setOnlineLink(e.target.value)}
                      type="text"
                      id="online_link"
                      className="form-input-2"
                    />
                    <p className="block text-xs font-medium text-red-600">
                      Only sent to buyers after payment. Never shown on the
                      public event page.
                    </p>
                  </div>
                </>
              )}

              {/* ---------------- Date and time ---------------- */}
              <div className="w-2/3 mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Date and time *
                </label>

                {/* the opening day - its date, and the hours it runs */}
                <div className="flex gap-3 mt-1">
                  <div className="w-1/3">
                    <label
                      htmlFor="event_date"
                      className="block text-xs font-medium text-gray-500"
                    >
                      Start date
                    </label>
                    <input
                      value={eventDate}
                      onChange={(e) => {
                        setEventDate(e.target.value);
                        // a blank or earlier end date follows the start, so
                        // a single day event just has both the same
                        if (!endDate || endDate < e.target.value) {
                          setEndDate(e.target.value);
                        }
                      }}
                      type="date"
                      id="event_date"
                      className="form-input-2"
                    />
                  </div>

                  <div className="w-1/3">
                    <label
                      htmlFor="start_time"
                      className="block text-xs font-medium text-gray-500"
                    >
                      Start time
                    </label>
                    <select
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      id="start_time"
                      className="form-input-2"
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="w-1/3">
                    <label
                      htmlFor="end_time"
                      className="block text-xs font-medium text-gray-500"
                    >
                      End time
                    </label>
                    <select
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      id="end_time"
                      className="form-input-2"
                    >
                      <option value="">Till late</option>
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* the closing day - its own hours, which need not match
                    the opening day */}
                <div className="flex gap-3 mt-3">
                  <div className="w-1/3">
                    <label
                      htmlFor="end_date"
                      className="block text-xs font-medium text-gray-500"
                    >
                      End date
                    </label>
                    <input
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      type="date"
                      id="end_date"
                      min={eventDate}
                      className="form-input-2"
                    />
                  </div>

                  <div className="w-1/3">
                    <label
                      htmlFor="end_day_start_time"
                      className="block text-xs font-medium text-gray-500"
                    >
                      Start time
                    </label>
                    <select
                      value={endDayStartTime}
                      onChange={(e) => setEndDayStartTime(e.target.value)}
                      id="end_day_start_time"
                      className="form-input-2"
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="w-1/3">
                    <label
                      htmlFor="end_day_end_time"
                      className="block text-xs font-medium text-gray-500"
                    >
                      End time
                    </label>
                    <select
                      value={endDayEndTime}
                      onChange={(e) => setEndDayEndTime(e.target.value)}
                      id="end_day_end_time"
                      className="form-input-2"
                    >
                      <option value="">Till late</option>
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <p className="block text-xs font-medium text-gray-700 mt-1">
                  For a one day event, set the end date to the same day.
                </p>
              </div>

              <div className="w-2/3 mt-4">
                <label
                  htmlFor="link_type"
                  className="block text-sm font-medium text-gray-700"
                >
                  Event Type
                </label>
                <select
                  value={linkType}
                  onChange={(e) => setLinkType(e.target.value)}
                  className="form-input-2"
                  id="link_type"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              {linkType === "public" && (
                <span className="text-red-600 text-xs">
                  Public means the event will be advertised on our website
                </span>
              )}

              <div className="w-2/3 mt-4">
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-gray-700 "
                >
                  Event Category
                </label>
                <select
                  value={categoryType}
                  onChange={(e) => setCategoryType(e.target.value)}
                  className="form-input-2"
                  id="category"
                >
                  <option value="Festivals & Concerts">
                    Festivals &amp; Concerts
                  </option>
                  <option value="Parties & Nightlife">
                    Parties &amp; Nightlife
                  </option>
                  <option value="Food & Drink">Food &amp; Drink</option>
                  <option value="Conferences & Summits">
                    Conferences &amp; Summits
                  </option>
                  <option value="Workshops & Trainings">
                    Workshops &amp; Trainings
                  </option>
                  <option value="Sports & Fitness">Sports &amp; Fitness</option>
                  <option value="Church & Fellowship">
                    Church &amp; Fellowship
                  </option>
                  <option value="Weddings & Introductions">
                    Weddings &amp; Introductions
                  </option>
                  <option value="Comedy & Theatre">Comedy &amp; Theatre</option>
                  <option value="Community & Fundraisers">
                    Community &amp; Fundraisers
                  </option>
                </select>
              </div>

              {/* ---------------- Tickets ---------------- */}
              <div className="w-2/3 mt-6">
                <label className="block text-sm font-medium text-gray-700">
                  Tickets *
                </label>

                {ticketTypes.length === 0 && (
                  <p className="text-xs text-red-600 mt-2">
                    No tickets yet. Add at least one before saving.
                  </p>
                )}

                {ticketTypes.map((ticket: TypeTicketType, key) => (
                  <div
                    key={key}
                    className="border border-gray-200 rounded-md px-3 py-3 mt-2 flex items-start justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {ticket.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {Number(ticket.price) > 0
                          ? "UGX " + Number(ticket.price).toLocaleString()
                          : "Free"}{" "}
                        per person
                        {Number(ticket.original_price) > 0 && (
                          <s className="ml-1">
                            UGX{" "}
                            {Number(ticket.original_price).toLocaleString()}
                          </s>
                        )}
                        {ticket.note ? " · " + ticket.note : ""}
                      </p>
                      <p className="text-xs text-gray-500">
                        {ticket.min_qty}–{ticket.max_qty} per order ·{" "}
                        {Number(ticket.capacity) > 0
                          ? Number(ticket.capacity).toLocaleString() +
                            " available"
                          : "unlimited"}
                        {ticket.sold != null && Number(ticket.sold) > 0
                          ? " · " +
                            Number(ticket.sold).toLocaleString() +
                            " sold"
                          : ""}
                      </p>
                    </div>

                    <div className="flex items-center">
                      <svg
                        onClick={() => {
                          setEditingTicketIndex(key);
                          setShowTicketTypeModal(true);
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        className="ml-2 text-blue-600 cursor-pointer"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1l1-4L16.5 3.5z"
                        />
                      </svg>
                      <svg
                        onClick={() => {
                          ticketTypeDispatch({
                            type: "remove",
                            payload: { index: key },
                          });
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        className="ml-1 text-red-600 cursor-pointer"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 7h16m-10 4v6m4-6v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"
                        />
                      </svg>
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => {
                    setEditingTicketIndex(null);
                    setShowTicketTypeModal(true);
                  }}
                  type="button"
                  className="btn-light mt-3"
                >
                  + Add ticket
                </button>
              </div>

              {/* ---------------- Banner ---------------- */}
              <div className="w-1/3 mt-6">
                <label
                  htmlFor="image"
                  className="block text-sm font-medium text-gray-700 "
                >
                  Event Banner *
                </label>

                {notEmpty(imageUrl) ? (
                  <p className="mt-2">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      className=" text-green-600"
                      href={storageUrl + "/" + imageUrl}
                    >
                      {" "}
                      {getFileNameFromUlr(imageUrl)}{" "}
                    </a>{" "}
                    <small
                      onClick={() => {
                        setImageUrl(null);
                      }}
                      className="text-red-600  cursor-pointer"
                    >
                      (change)
                    </small>
                  </p>
                ) : (
                  <DropFile
                    ref={dropFileRef}
                    getFileUrl={(fileUrl) => {
                      const validExtensions = [
                        "jpg",
                        "jpeg",
                        "png",
                        "gif",
                        "svg",
                        "webp",
                        "avif",
                      ];
                      const extension = fileUrl.split(".").pop()?.toLowerCase();

                      if (extension && validExtensions.includes(extension)) {
                        setImageUrl(fileUrl);
                      } else {
                        toast.error(
                          "Only image files (jpg, jpeg, png, gif, svg, webp, avif) are allowed."
                        );
                      }
                    }}
                    StorageFolder="events"
                    SubText="Event Banner"
                  />
                )}
              </div>
              {imageUrl && (
                <button
                  onClick={() => setShowLogoModal(true)}
                  type="button"
                  className="btn-light mt-2"
                >
                  Update event banner
                </button>
              )}

              {/* ---------------- More customizations ---------------- */}
              <div className="w-2/3 mt-6">
                <label className="block text-sm font-medium text-gray-700">
                  More customizations
                </label>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => {
                      setEditingInputIndex(null);
                      setShowExtraInputsModal(true);
                    }}
                    type="button"
                    className="btn-light "
                  >
                    + Extra Inputs
                  </button>
                </div>
              </div>

              {extraInputs.map((item: TypeExtraInput, key) => (
                <div className="w-2/3 mt-4" key={key}>
                  <label className="0 text-sm font-medium text-gray-700 flex items-center">
                    {item.label}

                    <svg
                      onClick={() => {
                        setEditingInputIndex(key);
                        setShowExtraInputsModal(true);
                      }}
                      xmlns="http://www.w3.org/2000/svg"
                      className="ml-2 text-blue-600 cursor-pointer"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1l1-4L16.5 3.5z"
                      />
                    </svg>
                    <svg
                      onClick={() => {
                        extraInputDispatch({
                          type: "remove",
                          payload: {
                            index: key,
                          },
                        });
                      }}
                      xmlns="http://www.w3.org/2000/svg"
                      className="ml-1 text-red-600 cursor-pointer"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 7h16m-10 4v6m4-6v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"
                      />
                    </svg>
                  </label>

                  {item.type === "text" && (
                    <input type="text" className="form-input-2" readOnly />
                  )}
                  {item.type === "multi-text" && (
                    <textarea
                      className="form-input-2"
                      cols={30}
                      rows={5}
                      readOnly
                    ></textarea>
                  )}
                  {item.type === "select" && (
                    <select className="form-input-2" disabled>
                      {item.options.map((option, key2) => (
                        <option key={key2} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                  {item.type === "checkbox" &&
                    item.options.map((option, key3) => (
                      <div key={key3} className="custom-check2 mt-1">
                        <input
                          type="checkbox"
                          id={"checkbox1" + key3}
                          disabled
                        />
                        <label
                          htmlFor={"checkbox1" + key3}
                          className="text-sm font-medium text-gray-700 "
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  {item.type === "radio" &&
                    item.options.map((option, key4) => (
                      <div key={key4} className="custom-radio mt-1">
                        <input
                          name="radio"
                          type="radio"
                          id={"radio" + key4}
                          disabled
                        />
                        <label
                          htmlFor={"radio" + key4}
                          className="text-sm font-medium text-gray-700 "
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                </div>
              ))}

              <LogoModal
                isOpen={showLogoModal}
                onClose={() => setShowLogoModal(false)}
              />

              <TicketTypeModal
                isOpen={showTicketTypeModal}
                onClose={() => {
                  setShowTicketTypeModal(false);
                  setEditingTicketIndex(null);
                }}
                ticketTypeAdded={(ticketType, index) =>
                  ticketTypeAdded(ticketType, index)
                }
                initialTicket={
                  editingTicketIndex !== null
                    ? ticketTypes[editingTicketIndex]
                    : undefined
                }
                editingIndex={editingTicketIndex}
              />

              <AddtionalInputsModal
                onClose={() => {
                  setShowExtraInputsModal(false);
                  setEditingInputIndex(null);
                }}
                isOpen={showExtraInputsModal}
                extraInputAdded={(extraInput, index) =>
                  extraInputAdded(extraInput, index)
                }
                initialInput={
                  editingInputIndex !== null
                    ? extraInputs[editingInputIndex]
                    : undefined
                }
                editingIndex={editingInputIndex}
              />

              <AppButton
                callBackFun={() => onSaveEventTicket()}
                showLoader={savingLinkStatus}
                spinnerClass="inline w-3 h-3 mr-2 text-prim-yellow animate-spin fill-black"
                className="prim-btn-1 mt-6"
                text={btnLabelText}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default NewEventsTicketItem;
